'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';

import { Link } from '@/core/i18n/navigation';
import { cn } from '@/shared/lib/utils';

type Countdown = {
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

function getCountdown(endAtMs: number): Countdown {
  const totalMs = Math.max(0, endAtMs - Date.now());
  const totalSeconds = Math.floor(totalMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { totalMs, days, hours, minutes, seconds };
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

const CLOSED_KEY = 'zestygen_promo_banner_closed_v1';
const END_AT_KEY = 'zestygen_promo_banner_end_at_v1';

export function PromoBanner({
  className,
}: {
  className?: string;
}) {
  const t = useTranslations('common.promo_banner');
  const ref = useRef<HTMLDivElement | null>(null);
  const searchParams = useSearchParams();

  const enabled = process.env.NEXT_PUBLIC_PROMO_BANNER_ENABLED !== 'false';
  const ctaHref = process.env.NEXT_PUBLIC_PROMO_BANNER_CTA_URL || '/pricing';

  const [isClosed, setIsClosed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [endAtMs, setEndAtMs] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(0);

  const promo = searchParams?.get('promo') || '';
  // Debug/test mode:
  // - ?promo=1/show/debug/reset => force show
  // - ?promo=1/reset => also force restart countdown (do not persist)
  const forceShow =
    promo === '1' || promo === 'show' || promo === 'debug' || promo === 'reset';
  const forceRestart = promo === '1' || promo === 'reset';

  const durationSec = Math.max(
    60,
    Number(process.env.NEXT_PUBLIC_PROMO_BANNER_DURATION_SEC) || 3600
  );

  // Avoid SSR hydration mismatch: server renders a stable 01:00:00 placeholder,
  // then client replaces it with the real countdown after mount.
  const countdown = useMemo(() => {
    if (!endAtMs) {
      return { totalMs: durationSec * 1000, days: 0, hours: 1, minutes: 0, seconds: 0 };
    }
    return getCountdown(endAtMs);
  }, [endAtMs, nowTick, durationSec]);

  const isVisible =
    enabled &&
    (!isClosed || forceShow) &&
    (!mounted || countdown.totalMs > 0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setMounted(true);
    if (forceShow) {
      // Debug mode: do not honor "closed" flag.
      setIsClosed(false);
      return;
    }
    try {
      setIsClosed(localStorage.getItem(CLOSED_KEY) === '1');
    } catch {
      setIsClosed(false);
    }
  }, [forceShow]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!enabled) return;
    if (!mounted) return;
    if (isClosed && !forceShow) return;

    const now = Date.now();
    // Debug/test: always restart to 1 hour, do NOT persist.
    if (forceShow && forceRestart) {
      setEndAtMs(now + durationSec * 1000);
      setNowTick(0);
      return;
    }

    // Normal mode: persist endAt so refresh won't reset countdown.
    let stored = 0;
    try {
      stored = Number(localStorage.getItem(END_AT_KEY) || 0);
    } catch {
      stored = 0;
    }
    const nextEndAt = stored > now ? stored : now + durationSec * 1000;
    if (nextEndAt !== stored) {
      try {
        localStorage.setItem(END_AT_KEY, String(nextEndAt));
      } catch {
        // ignore
      }
    }
    setEndAtMs(nextEndAt);
  }, [mounted, enabled, isClosed, forceShow, forceRestart, durationSec]);

  // drive countdown updates (do NOT depend on isVisible; we want it to tick as soon as it's mounted)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!mounted) return;
    if (!enabled) return;
    if (isClosed && !forceShow) return;

    const id = window.setInterval(() => setNowTick((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, [mounted, enabled, isClosed, forceShow]);

  // keep header/content offset in sync
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;

    function setHeightVar(px: number) {
      root.style.setProperty('--promo-banner-h', `${px}px`);
    }

    if (!isVisible) {
      setHeightVar(0);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver(() => setHeightVar(el.offsetHeight || 0));
    ro.observe(el);
    setHeightVar(el.offsetHeight || 0);
    return () => ro.disconnect();
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={ref}
      className={cn(
        'fixed inset-x-0 top-0 z-[60] w-full border-b border-white/10 bg-gradient-to-r from-[#0b1220] via-[#0f1b33] to-[#0b1220] text-white',
        className
      )}
      role="region"
      aria-label={t('aria_label')}
    >
      <div className="container flex items-center gap-3 py-2.5">
        {/* countdown */}
        <div className="hidden items-center gap-1.5 sm:flex">
          <TimeBox value={pad2(Math.min(99, countdown.days))} label={t('d')} />
          <span className="text-white/50">:</span>
          <TimeBox value={pad2(countdown.hours)} label={t('h')} />
          <span className="text-white/50">:</span>
          <TimeBox value={pad2(countdown.minutes)} label={t('m')} />
          <span className="text-white/50">:</span>
          <TimeBox value={pad2(countdown.seconds)} label={t('s')} />
        </div>

        {/* message */}
        <div className="flex min-w-0 flex-1 items-center justify-center gap-2 text-center">
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold tracking-wide">
            {t('badge')}
          </span>
          <span className="min-w-0 truncate text-sm font-medium">
            {t('message')}
          </span>
          <span className="hidden text-sm font-semibold text-yellow-200 md:inline">
            {t('highlight')}
          </span>
        </div>

        {/* CTA + close */}
        <div className="flex items-center gap-2">
          <Link
            href={ctaHref}
            className="rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-black shadow-sm transition hover:bg-white/90"
            prefetch={false}
          >
            {t('cta')}
          </Link>
          <button
            className="rounded-full p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
            aria-label={t('close')}
            onClick={() => {
              setIsClosed(true);
              try {
                localStorage.setItem(CLOSED_KEY, '1');
              } catch {
                // ignore
              }
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TimeBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-end gap-1 rounded-md bg-black/35 px-2 py-1 ring-1 ring-white/10">
      <span suppressHydrationWarning className="text-sm font-bold tabular-nums">
        {value}
      </span>
      <span className="pb-[1px] text-[11px] font-semibold text-white/70">
        {label}
      </span>
    </div>
  );
}


